import { X, Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormCheckbox, FormInput, FormSelect } from "@/components/forms";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";

const schema = z.object({
  label: z.string({ required_error: "Label is required" }).min(1),
  dataType: z.coerce
    .string({
      required_error: "Data type is required",
      invalid_type_error: "Data type must be a string",
    })
    .min(1, { message: "Please select a data type" }),
  defaultValue: z.any().optional(),
  isUnique: z.boolean().optional(),
  isRequired: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

interface AddAttributeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataTypes: any[];
  onSubmit: (data: any, form: any) => void;
  isLoading?: boolean;
}

export function AddAttributeSheet({
  dataTypes = [],
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: AddAttributeSheetProps) {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      label: "",
      dataType: "",
      defaultValue: "",
      isUnique: false,
      isRequired: false,
      isActive: true,
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
  };

  const handleSubmit = (data: any) => {
    onSubmit(data, form);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="aero:sm:max-w-[600px] aero:p-0 aero:gap-0 aero:flex aero:flex-col"
      >
        {/* Custom close button - hide default */}
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <SheetHeader className="aero:p-6 aero:pb-4 aero:gap-3 aero:border-b aero:border-neutral-200">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              Add New Attribute
            </SheetTitle>
            <SheetClose asChild>
              <button
                onClick={handleClose}
                className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100"
              >
                <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-5">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="aero:flex aero:flex-col aero:gap-5"
            >
              {/* Section: Attribute Information */}
              <div className="aero:flex aero:flex-col aero:gap-5">
                <div className="aero:flex aero:items-center aero:gap-2">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Attribute Information
                  </h3>
                </div>

                <div className="aero:flex aero:flex-col aero:gap-4">
                  <FormInput
                    name="label"
                    label="Attribute Label"
                    placeholder="Enter attribute label"
                    control={form.control}
                    required
                  />
                  <FormSelect
                    name="dataType"
                    label="Data Type"
                    placeholder="Select data type"
                    triggerClassName="aero:w-full"
                    options={
                      dataTypes?.map((i: any) => ({
                        label: i.displayName,
                        value: i.id,
                      })) || []
                    }
                    control={form.control}
                    required
                  />
                </div>
              </div>

              {/* Section: Options */}

              <div className="aero:flex aero:items-center aero:gap-3">
                <FormCheckbox
                  name="isUnique"
                  label="Unique"
                  control={form.control}
                />
                <FormCheckbox
                  name="isRequired"
                  label="Required"
                  control={form.control}
                />
                <FormCheckbox
                  name="isActive"
                  label="Active"
                  control={form.control}
                />
              </div>
            </form>
          </Form>
        </div>

        {/* Footer */}
        <SheetFooter className="aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:flex aero:flex-row aero:justify-end aero:gap-3">
          <Button
            type="button"
            intent="neutral"
            design="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isLoading}
            className="aero:h-10 aero:gap-2 aero:px-4 aero:bg-[#2F4F4F] hover:aero:bg-[#1F3F3F]"
          >
            {isLoading ? (
              <>
                <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                Creating...
              </>
            ) : (
              <>
                <Check className="aero:w-5 aero:h-5" />
                Create Attribute
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
